import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Alert } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { InfoButtonIcon } from 'src/component/Buttons/InfoButtonIcon';
import { useFilterCountries } from 'src/hooks/useFilterCountries';
import { useFilterProjects } from 'src/hooks/useFilterProjects';
import { useTpes } from 'src/hooks/useTpes';
import Breadcrumb from '../../breadcrumb';
import filterStyles from '../../styles/style-setting.module.scss';
import styles from '../../styles/style-project.module.scss';
import TpeColumns from './table-header/tpe-columns';

const NUMBER_OF_TPES_PER_PAGE = 15;

interface FilterOption {
  id: number;
  name: string;
}

const getCountry = (tpe): FilterOption | null => tpe.project?.country ?? null;

const ListTpePage = (props) => {
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState<number | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(0);
  const tpePerPage = NUMBER_OF_TPES_PER_PAGE;
  const pagesVisited = pageNumber * NUMBER_OF_TPES_PER_PAGE;

  const { tpes } = useTpes();
  const { projects } = useFilterProjects();
  const { countries } = useFilterCountries();

  const filteredTpes = useMemo(
    () =>
      tpes.filter(
        (tpe) =>
          (tpe.name?.toUpperCase().includes(search.toUpperCase()) ||
            tpe.code?.toUpperCase().includes(search.toUpperCase())) &&
          (projectId == null || tpe.project?.id === projectId) &&
          (countryId == null || getCountry(tpe)?.id === countryId)
      ),
    [tpes, search, projectId, countryId]
  );

  const isAnyFilterActive = !!search || projectId != null || countryId != null;

  const resetFilters = () => {
    setSearch('');
    setProjectId(null);
    setCountryId(null);
    setPageNumber(0);
  };

  let data = [];

  filteredTpes.slice(pagesVisited, pagesVisited + tpePerPage).map((tpe) => {
    let action = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Bouton DÉTAIL */}
        <Link to={`/tpe/detail/${tpe.id}`}>
          <InfoButtonIcon title={props.t('more_details')} className={`fa fa-eye ${styles['action']}`} />
        </Link>

        {/* Bouton EDIT */}
        <Link to={`/tpe/edit/${tpe.id}`}>
          <EditOutlinedIcon
            sx={{
              cursor: 'pointer',
              color: '#2196f3',
              fontSize: 22,
              display: 'flex',
              alignItems: 'center',
            }}
          />
        </Link>
      </div>
    );

    return data.push({
      code: tpe.code,
      name: tpe.name,
      type: tpe.type,
      anneeCreation: tpe.anneeCreation,
      action: action,
    });
  });

  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };

  const pageCount = Math.ceil(filteredTpes.length / tpePerPage);

  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('tpe.list')} url={'tpe/list'} />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardBody>
                  <div className={filterStyles['filterPanel']}>
                    <div className={filterStyles['filterHeader']}>
                      <span className={filterStyles['filterTitle']}>{props.t('filter.title')}</span>
                      {isAnyFilterActive && (
                        <button type="button" className={filterStyles['resetBtn']} onClick={resetFilters}>
                          ↺ {props.t('filter.reset')}
                        </button>
                      )}
                    </div>

                    <div className={filterStyles['filterBody']}>
                      <div className="row">
                        <div className="col-12 col-sm-6 col-lg-4 mb-3">
                          <label className={filterStyles['filterLabel']}>{props.t('tpe.search')}</label>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) => {
                              setSearch(event.target.value);
                              changePage({ selected: 0 });
                            }}
                            value={search}
                            placeholder={props.t('tpe.search')}
                          />
                        </div>

                        <div className="col-6 col-sm-3 col-lg-4 mb-3">
                          <label className={filterStyles['filterLabel']}>{props.t('production_cycle.project')}</label>
                          <select
                            className="form-control form-control-sm"
                            value={projectId ?? ''}
                            onChange={(event) => {
                              setProjectId(event.target.value ? Number(event.target.value) : null);
                              changePage({ selected: 0 });
                            }}
                          >
                            <option value="">{props.t('filter.allProjects')}</option>
                            {projects.map((project: FilterOption) => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-6 col-sm-3 col-lg-4 mb-3">
                          <label className={filterStyles['filterLabel']}>{props.t('tpe.country')}</label>
                          <select
                            className="form-control form-control-sm"
                            value={countryId ?? ''}
                            onChange={(event) => {
                              setCountryId(event.target.value ? Number(event.target.value) : null);
                              changePage({ selected: 0 });
                            }}
                          >
                            <option value="">{props.t('filter.allCountries')}</option>
                            {countries.map((country: FilterOption) => (
                              <option key={country.id} value={country.id}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {data.length === 0 ? (
                    <Alert variant="outlined" severity="info">
                      {props.t('tpe.no_data')}
                    </Alert>
                  ) : (
                    <TpeColumns data={data} />
                  )}
                  <br />
                  <div className="float-right">
                    {filteredTpes.length > NUMBER_OF_TPES_PER_PAGE && (
                      <ReactPaginate
                        previousLabel="<"
                        nextLabel=">"
                        pageCount={pageCount}
                        forcePage={pageNumber}
                        onPageChange={changePage}
                        containerClassName={styles['paginationButton']}
                        previousLinkClassName={styles['previousBtn']}
                        nextLinkClassName={styles['nextBtn']}
                        disabledClassName={styles['paginationDisabled']}
                        activeClassName={styles['paginationActive']}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};

export default translate(ListTpePage);
